import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import MenuButton from '../table/MenuButton';
import Popover from '../ui/Popover';
import Badge from '../ui/Badge';
import type { CustomerWorkType, EngagementType } from '../../types';

/** The five engagement types, in the product order. */
export const WORK_TYPE_ORDER: EngagementType[] = [
  'project', 'development', 'service_contract', 'hour_bank', 'service_without_contract',
];

/** "בנק · 12.5 ש'" — one decimal, as the hour-bank tag shows it. */
// DQ-082 (D-313): one decimal, but a quarter hour stays a quarter (0.25 / 1.75, not 0.3 / 1.8 - the bank page shows 0.25).
export const oneDecimal = (n: number | null | undefined) => {
  const x = Number(n ?? 0);
  const two = x.toFixed(2);
  return two.endsWith('0') ? x.toFixed(1) : two;
};

/**
 * P-01/B2 — the "סוגי עבודה" tags of one customer: one tag per engagement type
 * that has an active / on-hold engagement. Each tag opens a small menu of
 * those engagements (name + status); a row opens the project page when the
 * engagement is a project with its project row, otherwise the engagement page.
 * The tags sit over the row's stretched link (`dt-above`).
 */
export function WorkTypeTags({ workTypes, inCharacterization = false }: {
  workTypes: CustomerWorkType[] | undefined;
  /** P-07 (D-74 §5) — an active "אפיון" card and no open engagement yet: a clear "באפיון" tag. */
  inCharacterization?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = workTypes ?? [];
  const chTag = inCharacterization ? (
    <span className="inline-flex items-center whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-800">
      {t('characterization.inCharacterization')}
    </span>
  ) : null;
  if (list.length === 0) return chTag ? <span className="flex flex-wrap gap-1">{chTag}</span> : <span className="text-text-3">—</span>;

  const tagLabel = (w: CustomerWorkType) =>
    w.type === 'hour_bank'
      ? t('organizations.workTypeTag.hour_bank', { hours: oneDecimal(w.hour_bank_balance) })
      : t(`organizations.workTypeTag.${w.type}`, { count: w.count });

  return (
    <span className="dt-above flex flex-wrap gap-1">
      {chTag}
      {list.map((w) => {
        const label = tagLabel(w);
        return (
          <MenuButton
            key={w.type}
            label={t('organizations.workTypeDialog', { type: label })}
            align="start"
            popoverClassName="w-64 p-1.5"
            triggerClassName="inline-flex items-center whitespace-nowrap rounded-full border border-border bg-surface-subtle px-2 py-0.5 text-xs font-medium text-text hover:bg-surface aria-expanded:bg-surface"
            items={w.engagements.map((e) => ({
              key: e.id,
              label: e.name || '—',
              textValue: e.name ?? '',
              trailing: (
                <Badge tone={e.status === 'active' ? 'success' : 'warning'} dot>
                  {t(`engagements.statuses.${e.status}`)}
                </Badge>
              ),
              onSelect: () => navigate(w.type === 'project' && e.project_id ? `/projects/${e.project_id}` : `/engagements/${e.id}`),
            }))}
          >
            {label}
          </MenuButton>
        );
      })}
    </span>
  );
}

export interface EngagementFilterValue {
  types: EngagementType[];
  /** D-264 round 2 — one switch: only ACTIVE engagements of the chosen types. */
  activeOnly: boolean;
}

export const EMPTY_ENGAGEMENT_FILTER: EngagementFilterValue = {
  types: [], activeOnly: false,
};

export const engagementFilterCount = (f: EngagementFilterValue) =>
  f.types.length + Number(f.activeOnly);

/**
 * P-01/B3 — "סוג התקשרות" (multi-select of the five types). D-264: the three
 * extra switches that repeated project / service contract / hour bank were
 * removed — each type appears once. The values are sent to the server as GET
 * parameters; the list is filtered and paginated there.
 */
export function EngagementFilterControl({ value, onChange }: {
  value: EngagementFilterValue;
  onChange: (next: EngagementFilterValue) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const count = engagementFilterCount(value);

  const toggleType = (ty: EngagementType) => onChange({
    ...value,
    types: value.types.includes(ty) ? value.types.filter((x) => x !== ty) : WORK_TYPE_ORDER.filter((x) => x === ty || value.types.includes(x)),
  });
  const check = (checked: boolean, onToggle: () => void, label: string, key: string) => (
    <label key={key} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-text hover:bg-surface-subtle">
      <input type="checkbox" className="size-4 accent-primary-700" checked={checked} onChange={onToggle} />
      {label}
    </label>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((o) => !o)}
        className={`btn btn-secondary btn-sm ${count > 0 ? 'border-accent text-accent-text' : ''}`}
      >
        {t('organizations.filterEngagementType')}
        {count > 0 && <span className="tabular-nums">({count})</span>}
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        align="start"
        role="dialog"
        ariaLabel={t('organizations.filterEngagementType')}
        focusFirst
        className="w-64 p-2"
      >
        <fieldset>
          <legend className="mb-1 px-2 text-xs font-medium text-text-2">{t('organizations.filterEngagementType')}</legend>
          {WORK_TYPE_ORDER.map((ty) => check(value.types.includes(ty), () => toggleType(ty), t(`engagementDetail.types.${ty}`), ty))}
        </fieldset>
        <div className="my-1.5 border-t border-border" />
        {check(value.activeOnly, () => onChange({ ...value, activeOnly: !value.activeOnly }), t('organizations.filterActiveOnly'), 'ao')}
      </Popover>
    </>
  );
}
